import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { 
  ListSubheader, 
  Box, 
  List, 
  styled, 
  Button, 
  ListItem, 
  Typography,
  alpha,
  Collapse,
  IconButton
} from '@mui/material';
import { 
  CardGiftcard, 
  Category, 
  Dashboard, 
  EmojiEvents, 
  Loyalty, 
  People, 
  Quiz,
  Redeem,
  ExpandMore,
  ExpandLess,
  ChevronRight,
  Assessment,
  AccountCircle,
  Report,
  Payments,
  WorkspacePremium
} from '@mui/icons-material';
import { useState } from 'react';

const SidebarContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  borderRight: '1px solid',
  borderColor: alpha('#FFFFFF', 0.1),
  // padding: theme.spacing(3, 0),
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
  }
}));

const MenuSection = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha('#FFFFFF', 0.05),
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha('#ff5d1a', 0.5),
    borderRadius: '2px',
  }
}));

const SectionHeader = styled(ListSubheader)(({ theme }) => ({
  color: alpha('#FFFFFF', 0.5),
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  background: 'transparent',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:first-of-type': {
    marginTop: 0,
  }
}));

const MenuButton = styled(Button)(({ theme, active }) => ({
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(0.5, 2),
  borderRadius: '12px',
  justifyContent: 'flex-start',
  textTransform: 'none',
  color: active ? '#FFFFFF' : alpha('#FFFFFF', 0.7),
  background: active ? 'linear-gradient(90deg, #3399FF22, #3399FF11)' : 'transparent',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&::before': active ? {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '60%',
    borderRadius: '0 2px 2px 0',
  } : undefined,
  '&:hover': {
    background: alpha('#ff5d1a', 0.15),
    color: '#FFFFFF',
    borderColor: alpha('#ff5d1a', 0.3),
    transform: 'translateX(4px)',
  },
  '& .MuiButton-startIcon': {
    color: active ? '#ff5d1a' : alpha('#FFFFFF', 0.5),
    marginRight: theme.spacing(2),
    transition: 'all 0.2s ease',
  },
  '& .MuiButton-endIcon': {
    marginLeft: 'auto',
    color: alpha('#FFFFFF', 0.3),
  },
  '& .menu-text': {
    fontWeight: 300,
    fontSize: '0.875rem',
  },
  '& .MuiButton-endIcon > *:nth-of-type(1)': {
    fontSize: 10,
  },
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  color: alpha('#FFFFFF', 0.5),
  padding: theme.spacing(0.5),
  '&:hover': {
    background: alpha('#FFFFFF', 0.1),
    color: '#FFFFFF',
  }
}));

const Badge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'badge',
})(({ badge }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#FFFFFF',

  background:
    badge === 'NEW'
      ? 'linear-gradient(135deg, #00E5FF, #2979FF)'
      : 'linear-gradient(135deg, #FF6A00, #FF3D00)',
}));



function SidebarMenu() {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    manageExam: true,
    report: true,
    user: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuItems = [
    {
      section: 'dashboard',
      header: 'Dashboard',
      items: [
        { label: 'Dashboard', icon: <Dashboard />, path: 'main' },
      ]
    },
    {
      section: 'manageExam',
      header: 'Exam Management',
      items: [
        { label: 'Challenge', icon: <EmojiEvents />, path: 'exam-challenge' },
        { label: 'Paid Exam', icon: <WorkspacePremium />, path: 'paid-exam' },
        { label: 'Free Exam', icon: <Redeem />, path: 'free-exam' },
        { label: 'Questions', icon: <Quiz />, path: 'question' },
        { label: 'Categories', icon: <Category />, path: 'category' },
        { label: 'Subscription', icon: <Loyalty />, path: 'subscription' }
      ]
    },
    {
      section: 'manageReward',
      header: 'Reward Management',
      items: [
        { label: 'Reward', icon: <CardGiftcard />, path: 'reward', badge: 'NEW' },
        { label: 'Withdraw', icon: <Payments />, path: 'withdraw', badge: 'NEW'  },
      ]
    },
    {
      section: 'report',
      header: 'Reports',
      items: [
        { label: 'Reported Questions', icon: <Report />, path: 'report' },
        { label: 'Exam Analytics', icon: <Assessment />, path: 'analytics', badge: 'SOON'  }
      ]
    },
    {
      section: 'user',
      header: 'User Management',
      items: [
        { label: 'Users', icon: <People />, path: 'user' },
        { label: 'My Profile', icon: <AccountCircle />, path: 'manage/profile' }
      ]
    }
  ];

  return (
    <SidebarContainer>
      <MenuSection>
        {menuItems.map((menu) => (
          <Box key={menu.section}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
              <SectionHeader title={menu.header} sx={{ flexGrow: 1 }}>{menu.header}</SectionHeader>
              {['manageExam','manageReward', 'report', 'user'].includes(menu.section) && (
                <ExpandButton
                  size="small"
                  onClick={() => toggleSection(menu.section)}
                >
                  {expandedSections[menu.section] ? <ExpandLess /> : <ExpandMore />}
                </ExpandButton>
              )}
            </Box>
            
            <Collapse in={expandedSections[menu.section] ?? true}>
              <List component="div" disablePadding>
                {menu.items.map((item) => {
                  const isActive = location.pathname.includes(item.path);
                  return (
                    <ListItem key={item.path} disablePadding>
                      <MenuButton
                        fullWidth
                        component={RouterLink}
                        to={item.path}
                        startIcon={
                          <Box>
                            {item.icon}
                          </Box>
                        }
                        endIcon={
                          item.badge ? (
                            <Badge badge={item.badge}>{item.badge}</Badge>
                          ) : isActive ? (
                            <ChevronRight sx={{ fontSize: 8 }} />
                          ) : null
                        }
                        active={isActive}
                      >
                        <Typography className="menu-text">
                          {item.label}
                        </Typography>
                      </MenuButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </MenuSection>
    </SidebarContainer>
  );
}

export default SidebarMenu;